import React, { useState, useEffect, useRef } from 'react'

import { styled } from 'styled-components'
import usePlacesAutocomplete from 'use-places-autocomplete'

import { APP_DEFAULT_STATE } from '@api/local'
import { Icon, IconEnum, StaticImage } from '@atoms/images'
import { Paragraph, Spacer } from '@atoms/index'
import { ResponsivePXValue, theme } from '@components/Theme'
import { useGetAppQuery } from '@hooks/api'
import { FieldData, Form, SelectInput, useForm } from '@molecules/index'
import { DeviceTypeEnum } from '@uctypes/api/globalTypes'

const Container = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;

  form {
    width: 100%;
  }

  div.place-id-lookup {
    input {
      border-radius: 4px;
      border-width: 1px;
      ${ResponsivePXValue('padding-left', '30px')}
      ${ResponsivePXValue('height', '40px')}

      ${(props) => props.$isMobile && `
        ${ResponsivePXValue('width', '85%')};
      `}

      &::placeholder {
        font-weight: 400;
        color: ${(props): string => props.theme.colors.whites.silver};
      }
    }
    .right-icon {
      ${ResponsivePXValue('left', '5px')}
      ${ResponsivePXValue('bottom', '-3px')}
    }
  }
`

const ImageContainer = styled.div`
  display: flex;
  align-self: flex-end;
  ${ResponsivePXValue('width', '96px')}
  ${ResponsivePXValue('margin-top', '8px')}
  ${ResponsivePXValue('margin-right', '8px')}

  picture {
    display: flex;
  }

  img {
    max-width: 96px;
    ${ResponsivePXValue('max-width', '96px')}
  }
`

const IconSelectContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`
const MessageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  ${ResponsivePXValue('padding', { mobile: '0 8px', tablet: '0 8px', desktop: '0 8px' })};
`

const IconContainer = styled.div`
  position: absolute;
  z-index: 9;
  left: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  ${ResponsivePXValue('bottom', { mobile: '14px', tablet: '12px', desktop: '14px' })};
  ${ResponsivePXValue('width', { mobile: '16px', tablet: '20px', desktop: '18px' })};
  ${ResponsivePXValue('height', { mobile: '16px', tablet: '20px', desktop: '18px' })};
`

export interface PlaceIdOption {
  title: string
  value: string
  types: string[]
}
export interface PlaceIdLookupProps {
  onSelect: (placeId: string, option?: PlaceIdOption) => void
  resetKey?: string | number // triggers reset when changed

  isRequired?: boolean
  disabled?: boolean
}

interface PlaceIdLookupState {
  loadingAddress: boolean
  options: PlaceIdOption[]
}

const DEFAULT_STATE: PlaceIdLookupState = {
  loadingAddress: false,
  options: [] as PlaceIdOption[],
}

export function PlaceIdLookup({ onSelect, resetKey, disabled = false, isRequired = true }: PlaceIdLookupProps): JSX.Element {

  const [form] = useForm()
  const [state, setState] = useState<PlaceIdLookupState>({ ...DEFAULT_STATE })
  const { data: appData = { app: { ...APP_DEFAULT_STATE } } } = useGetAppQuery()
  const selectInputRef = useRef<HTMLInputElement | null>(null)

  const {
    ready,
    _,
    suggestions: { data },
    setValue,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: {
        country: 'ZA',
      },
      // types: ['address'],
    },
    debounce: 500,
  })

  const isMobile = appData.app.deviceType === DeviceTypeEnum.MOBILE

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isRequired && selectInputRef.current) {
        selectInputRef.current.focus()
      }
    }, 100)
    return () => clearTimeout(timer)
  }, [])

  useEffect((): void => {
    const options = data.map((location) => ({ title: location.description, value: location.place_id, types: location.types })) as PlaceIdOption[]
    setState((prevState) => ({ ...prevState, options }))
  }, [data])

  useEffect(() => {
    if (resetKey !== undefined) {
      form.resetFields()
      setValue('')
      setState({ ...DEFAULT_STATE })
    }
  }, [resetKey])

  const _handleChange = (changedFields: FieldData[]) => {
    changedFields.forEach((field) => {
      (field.name as string[]).forEach((name) => {
        if (name === 'address') {
          onSelect(field.value, state.options.find(o => o.value === field.value))
        }
      })
    })
  }

  return (
    <Container $isMobile={isMobile}>
      <Form form={form} onFieldsChange={_handleChange}>
        <IconSelectContainer >
          <IconContainer>
            <Icon className='location-outline' icon={IconEnum.LOCATION_OUTLINE} />
          </IconContainer>
          <SelectInput
            ref={selectInputRef}
            className='place-id-lookup'
            loading={!ready}
            name='address'
            label='Street name and number'
            placeholder='Enter street name and number'
            onSearchChange={setValue}
            options={state.options}
            rules={[{ required: isRequired, message: 'Please select an address' }]}
            disabled={disabled}
          />
        </IconSelectContainer>
        <Spacer universal='4px' />
        <MessageContainer>
          <Paragraph variant='p3' color={theme.colors.slates.ironsideGrey}>
          E.g. 43 Long street
          </Paragraph>
          <ImageContainer>
            <StaticImage
              staticId='powered-by-google'
              lazy={false}
              objectFit='contain'
              mobileWidth={256}
              tabletWidth={256}
              desktopWidth={256} />
          </ImageContainer>
        </MessageContainer>
      </Form>
    </Container>
  )

}
